import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle, Warning, Spinner, ShieldCheck, ShieldStar, SignIn } from "@phosphor-icons/react";

// Module-scoped guard — survives React 18 StrictMode remounts (which reset useRef).
// Ensures we never POST /verify-link twice for the same token in one tab.
const _consumed = new Set();

// Detect obvious link scanners / prefetchers so we NEVER auto-consume the
// token when a bot is loading the page. The real user still sees the
// "Continue to sign in" button and taps it.
function isLikelyLinkScanner() {
  if (typeof navigator === "undefined") return true;
  const ua = (navigator.userAgent || "").toLowerCase();
  const scannerHints = [
    "bot", "crawler", "spider", "preview", "scan",
    "safelinks", "defender", "prefetch", "urlscan",
    "headlesschrome", "phantomjs", "slurp",
  ];
  return scannerHints.some((h) => ua.includes(h));
}

export default function MagicLinkVerify() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { verifyLink, verifyTwoFa } = useAuth();
  // ready → verifying → success | 2fa | error
  const [state, setState] = useState("ready");
  const [error, setError] = useState("");
  const [twoFa, setTwoFa] = useState(null);          // { challenge_id, email }
  const [tfaCode, setTfaCode] = useState("");
  const [tfaSubmitting, setTfaSubmitting] = useState(false);

  const consume = async () => {
    if (!token) return;
    if (_consumed.has(token)) return;
    _consumed.add(token);
    setState("verifying");
    const r = await verifyLink(token);
    if (r.ok) {
      setState("success");
      setTimeout(() => navigate("/app", { replace: true }), 800);
    } else if (r.twoFa) {
      setTwoFa(r.twoFa);
      setState("2fa");
    } else {
      setState("error");
      setError(r.error);
    }
  };

  // If this session was previously verified in the same tab (e.g. StrictMode
  // remount after success), skip the button and race straight through.
  useEffect(() => {
    if (token && _consumed.has(token)) {
      consume();
    }
    // Bots that DO run JS: bail out. Real users hit the "Continue" button.
    if (isLikelyLinkScanner()) return;
  }, [token]);

  const submitTfa = async (e) => {
    e?.preventDefault?.();
    if (!twoFa || !tfaCode.trim()) return;
    setTfaSubmitting(true);
    setError("");
    const r = await verifyTwoFa(twoFa.challenge_id, tfaCode.trim());
    setTfaSubmitting(false);
    if (r.ok) {
      setState("success");
      setTimeout(() => navigate("/app", { replace: true }), 500);
    } else {
      setError(r.error);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-brand-navy px-6 py-12 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-70">
        <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(0,199,255,0.10) 0%, transparent 60%)" }} />
      </div>
      <div className="relative w-full max-w-md z-10">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden"
          data-testid="verify-card"
          style={{ boxShadow: "0 25px 60px -12px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05)" }}>
          <div className="bg-brand-navy px-8 py-8 flex items-center justify-center">
            <Logo size={100} />
          </div>
          <div className="px-8 pt-10 pb-10 text-center">
            {state === "ready" && (
              <>
                <SignIn size={44} weight="duotone" className="text-brand-navy mx-auto mb-4" />
                <h1 className="font-display text-2xl font-bold tracking-tight text-brand-navy"
                    data-testid="verify-ready-title">
                  Confirm sign-in
                </h1>
                <p className="text-sm text-slate-500 mt-3 leading-relaxed">
                  Tap the button below to complete your secure sign-in.
                </p>
                <button
                  type="button"
                  onClick={consume}
                  data-testid="verify-continue-btn"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-brand-navy hover:bg-brand-navy/90 text-white font-semibold transition-colors">
                  <SignIn size={16} weight="bold" />
                  Continue to sign in
                </button>
                <p className="text-[11px] text-slate-400 mt-5 leading-relaxed">
                  This one-tap step keeps corporate email scanners
                  (Safe Links / Defender) from consuming your link before you use it.
                </p>
              </>
            )}
            {state === "verifying" && (
              <>
                <Spinner size={44} weight="bold" className="text-brand-navy mx-auto mb-4 animate-spin" />
                <h1 className="font-display text-2xl font-bold tracking-tight text-brand-navy">Signing you in…</h1>
                <p className="text-sm text-slate-500 mt-2">One moment.</p>
              </>
            )}
            {state === "2fa" && (
              <form onSubmit={submitTfa} data-testid="verify-2fa-form" className="text-left">
                <div className="text-center">
                  <ShieldStar size={44} weight="duotone" className="text-brand-navy mx-auto mb-3" />
                  <h1 className="font-display text-2xl font-bold tracking-tight text-brand-navy">
                    Two-factor code
                  </h1>
                  <p className="text-sm text-slate-500 mt-2">
                    Enter the 6-digit code from your authenticator app.
                  </p>
                  {twoFa?.email && (
                    <p className="text-[11px] text-slate-400 mt-2">
                      Signing in as <span className="font-semibold">{twoFa.email}</span>
                    </p>
                  )}
                </div>
                <label htmlFor="tfa-code" className="block mt-6 mb-1.5 text-[11px] font-semibold tracking-widest uppercase text-slate-500">
                  Verification code
                </label>
                <input id="tfa-code"
                  inputMode="text"
                  maxLength={15}
                  autoFocus
                  autoComplete="one-time-code"
                  value={tfaCode}
                  onChange={(e) => setTfaCode(e.target.value)}
                  data-testid="verify-2fa-input"
                  placeholder="123 456"
                  className="w-full px-4 py-3 rounded-md border border-slate-300 bg-white font-mono text-center text-lg tracking-[0.4em] focus:outline-none focus:ring-2 focus:ring-brand-cyan/40 focus:border-brand-cyan transition-colors" />
                <p className="mt-1.5 text-[11px] text-slate-400">
                  Lost your device? Use one of your XXXXX-XXXXX backup codes.
                </p>
                {error && (
                  <div className="mt-3 text-xs text-red-600" data-testid="verify-2fa-error">{error}</div>
                )}
                <button type="submit"
                  disabled={tfaSubmitting || tfaCode.trim().length < 6}
                  data-testid="verify-2fa-submit"
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-brand-navy hover:bg-brand-navy/90 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  {tfaSubmitting ? "Verifying…" : "Verify & sign in"}
                </button>
                <Link to="/login" data-testid="verify-2fa-cancel"
                  className="mt-4 block text-center text-xs text-slate-500 hover:text-brand-navy">
                  Cancel and sign in with a new link
                </Link>
              </form>
            )}
            {state === "success" && (
              <>
                <CheckCircle size={48} weight="duotone" className="text-emerald-500 mx-auto mb-4" />
                <h1 className="font-display text-2xl font-bold tracking-tight text-brand-navy" data-testid="verify-success">Signed in</h1>
                <p className="text-sm text-slate-500 mt-2">Redirecting to your dashboard…</p>
              </>
            )}
            {state === "error" && (
              <>
                <Warning size={48} weight="duotone" className="text-red-500 mx-auto mb-4" />
                <h1 className="font-display text-2xl font-bold tracking-tight text-brand-navy" data-testid="verify-error-title">This link can&apos;t be used</h1>
                <p className="text-sm text-slate-600 mt-3" data-testid="verify-error">{error}</p>
                <p className="text-xs text-slate-400 mt-6 leading-relaxed">
                  Magic links are single-use and expire after 15 minutes.
                </p>
                <Link to="/login" data-testid="verify-back-to-login"
                  className="inline-block mt-5 text-sm font-semibold text-brand-navy hover:text-brand-cyan underline underline-offset-4">
                  Request a new link
                </Link>
              </>
            )}
          </div>
          <div className="px-8 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-center gap-2">
            <ShieldCheck size={14} weight="fill" className="text-brand-navy" />
            <p className="text-[11px] font-semibold tracking-wider uppercase text-slate-500">
              Encrypted &middot; Audited &middot; Single-use
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
